/**
 * MUI LoadingDocks component that replaces the styled-components LoadingDocks component
 * 
 * This component provides a Material UI equivalent of the original LoadingDocks component
 * with similar props and functionality.
 */
import React, { Fragment, useContext } from "react";
import { styled } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { getConditionResult } from "reduxModules/selectors/containerSelectors";
import { getDrawingBox } from "reduxModules/ducks/ui";
import ToolContext from "components/services/ToolContext";
import { keys } from "lodash";
import { 
  MuiLoadingDocksProps, 
  DockProps,
  DockGroupProps,
  DockPathProps,
  LoadingDocksSides
} from "types/components/elements";
import { RootState } from "types/redux/state";

/**
 * MUI LoadingDocks component
 */
const MuiLoadingDocks: React.FC<MuiLoadingDocksProps> = (props) => {
  const sides = useSelector((state: RootState) => state.container.loadingDocks);
  // if zoom and in area use only that side 
  const { bbox, scale, zoom } = useSelector(getDrawingBox);

  const activeTab = useSelector((state: RootState) => state.container.activeTab);  
  const showDocks: LoadingDocksSides = zoom 
    ? keys(sides).reduce((acc, key) => ({ ...acc, [key]: activeTab.includes(key) }), {}) as LoadingDocksSides 
    : { ...sides };

  // fixme this needs to account for part of the area that is not there 
  
  const ghosts = useSelector((state: RootState) => state.container.ghostDocks);
  
  const context = useContext(ToolContext);
  const bay = context.constants.container.baySize * scale;
  let qty = bbox.width / bay;
  const hqty = (qty % 1 === 0 ? qty - 1 : parseInt(qty.toString())) - (Number(sides.left_docks) + Number(sides.right_docks)); // 2
  const hstart = bbox.cx - (hqty + Number(sides.right_docks) - Number(sides.left_docks)) * bay / 2; // hqty * bay/2;
  const horizontal = Array.from({ length: hqty }, (_, index) => hstart + index * bay);

  qty = bbox.height / bay;
  const vqty = (qty % 1 === 0 ? qty - 1 : parseInt(qty.toString())) - (Number(sides.top_docks) + Number(sides.bottom_docks));
  // fixme
  const vstart = bbox.cy - (vqty + Number(sides.bottom_docks) - Number(sides.top_docks)) * bay / 2;
  const vertical = Array.from({ length: vqty }, (_, index) => vstart + index * bay);

  const area = props.children;
  const visible = useSelector((state: RootState) => getConditionResult(state, { ...props.show, area: area.id }));
  const active = useSelector((state: RootState) => getConditionResult(state, { ...props.active, area: area.id }));

  return (
    visible && (
      <Group id="loadingdocks">
        {(sides.top_docks || sides.bottom_docks) && horizontal.map((pos, index) => {
          const top = `top-${index}`;
          const bottom = `bottom-${index}`;
          const dockProps = {
            active: active,
            bay: bay,
            x: pos,
            scale: scale
          };
          return (
            <Fragment key={`tb-${index}`}>
              {showDocks.top_docks && (
                <Dock 
                  key={top} 
                  id={top} 
                  {...dockProps} 
                  ghost={ghosts.includes(top)} 
                  y={bbox.y0} 
                  rotate={180} 
                />
              )}
              {showDocks.bottom_docks && (
                <Dock 
                  key={bottom} 
                  id={bottom} 
                  {...dockProps} 
                  ghost={ghosts.includes(bottom)} 
                  y={bbox.y1} 
                  rotate={0} 
                />
              )}
            </Fragment>
          );
        })}

        {(sides.left_docks || sides.right_docks) && vertical.map((pos, index) => {
          const left = `left-${index}`;
          const right = `right-${index}`;
          const dockProps = {
            active: active,
            bay: bay,
            y: pos,
            scale: scale
          };
          return (
            <Fragment key={`lr-${index}`}>
              {showDocks.left_docks && (
                <Dock 
                  key={left} 
                  id={left} 
                  {...dockProps} 
                  ghost={ghosts.includes(left)} 
                  x={bbox.x0} 
                  rotate={90} 
                />
              )}
              {showDocks.right_docks && (
                <Dock 
                  key={right} 
                  id={right} 
                  {...dockProps} 
                  ghost={ghosts.includes(right)} 
                  x={bbox.x1} 
                  rotate={-90} 
                />
              )}
            </Fragment>
          );
        })}
      </Group>
    )
  );
};

// Styled components using MUI styled API
const Group = styled('g')({});

/**
 * Dock component
 */
const Dock: React.FC<DockProps> = (props) => {
  return (
    <DockGroup 
      className="dock"
      id={props.id}
      transform={`translate(${props.x} ${props.y}) rotate(${props.rotate}) translate(-${Number([-90, 180].includes(props.rotate)) * props.bay} 0)`}
      active={props.active}
      ghost={props.ghost}
    >
      {(props.active || !props.ghost) && <DockPath scale={props.scale} />}
    </DockGroup>
  );
};

const DockGroup = styled('g', {
  shouldForwardProp: (prop) => !['active', 'ghost'].includes(prop as string),
})<DockGroupProps>`
  fill: none;
  stroke: ${({ active }) => active ? 'var(--app-red)' : 'var(--app-dark-gray)'};
  stroke-width: ${({ ghost }) => ghost ? 0.5 : 1}px;
  stroke-dasharray: ${({ ghost }) => ghost ? 2 : 0};
  shape-rendering: crispedges;
`;

const DockPath = styled('path', {
  shouldForwardProp: (prop) => prop !== 'scale',
})<DockPathProps>(({ scale }) => {
  const start = 0.6096 * scale;
  const short = 0.3048 * scale;
  const long = 2.4384 * scale;
  return {
    d: `m${start} 0h-${short}v-${short}h${short}zv-${long}h${long}v${long}zl${long}-${long}m-${long} 0l${long} ${long}h${short}v-${short}h-${short}`,
  };
});

export default MuiLoadingDocks;
